import React from "react";
import {
  Box,
  Button,
  Divider,
  FormControl,
  IconButton,
  MenuItem,
  Modal,
  Select,
  SelectChangeEvent,
  SxProps,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

import { Body2Regular, COLORS, TYPOGRAPHY_COMMON_STYLES } from "components";
import { EsgArrowDown } from "assets";
import { siteSelectorStyle } from "./styles";

export const SiteSelector = ({
  isOpen,
  onClose,
  sx,
  sitesList,
  currentSiteSelected,
  setCurrentSiteSelected,
  handleNextClick,
}: {
  isOpen: boolean;
  onClose: () => void;
  sx?: SxProps;
  sitesList: { siteId: number, siteName: string }[];
  currentSiteSelected: { siteId: number, siteName: string } | null;
  setCurrentSiteSelected: React.Dispatch<React.SetStateAction<{ siteId: number, siteName: string } | null>>;
  handleNextClick: () => void;
}) => {
  const handleSiteChange = (event: SelectChangeEvent<string>) => {
    const siteSelected = sitesList.find((site) => site.siteName === event.target.value);
    if (siteSelected) {
      setCurrentSiteSelected(siteSelected);
    }
  };

  return (
    <Modal open={isOpen} onClose={onClose} disableAutoFocus>
      <Box sx={{ ...siteSelectorStyle, ...sx } as SxProps}>
        <Box sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          py: "12px",
          px: "24px",
          pr: "16px",
        }}>
          <Typography variant="h6" sx={{ fontSize: "12px", fontWeight: "600", color: COLORS.Mainblue }}>
            Step 1/2
          </Typography>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
        <Divider sx={{ borderColor: COLORS.Mainblue }} />
        <Box sx={{
          height: "273px",
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
        }}>
          <Box sx={{
            width: "100%",
            display: "flex",
            flexDirection: "column",
            gap: "16px",
            justifyContent: "space-between",
            alignItems: "flex-start",
            py: "32px",
            px: "32px",
          }}>
            <Typography variant="body1" component="p" sx={{ fontSize: "20px", fontWeight: "500", color: COLORS.Mainblue }}>
              Select Site
            </Typography>
            <Box sx={{ width: "100%" }}>
              <Typography variant="body1" component="p" sx={{ fontSize: "12px", fontWeight: "600", color: COLORS.Mainblue }}>
                Site
              </Typography>
              <FormControl sx={{ width: "100%", mt: "8px" }}>
                <Select
                  displayEmpty
                  sx={{
                    height: "36px",
                    borderRadius: "8px",
                    fontFamily: "Inter",
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: COLORS.Romance,
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: COLORS.Romance,
                    },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderColor: COLORS.Romance,
                    },
                    "& .MuiSelect-icon": {
                      fontSize: "28px",
                      color: COLORS.Mainblue,
                      right: "12px",
                      top: "50%",
                      transform: "translateY(-50%)",
                      position: "absolute",
                      pointerEvents: "none",
                    },
                    "& .MuiSelect-select": {
                      fontSize: "14px",
                      color: COLORS.Romance,
                      fontWeight: 500,
                    },
                  }}
                  labelId="site-label"
                  id="site-select"
                  value={currentSiteSelected?.siteName ?? ""}
                  onChange={handleSiteChange}
                  placeholder="No site selected"
                  renderValue={(selected) => {
                    if (!selected) {
                      return <Body2Regular sx={{ color: COLORS.Romance, mt: "1px" }}>Select Site</Body2Regular>;
                    }
                    return selected;
                  }}
                  IconComponent={(props) => (
                    <Box sx={{ pointerEvents: "none", display: "flex", alignItems: "center", mr: 2 }}>
                      <EsgArrowDown {...props} />
                    </Box>
                  )}
                >
                  {sitesList && sitesList.map((site: { siteId: number, siteName: string }) => (
                    <MenuItem key={site.siteId} value={site.siteName} sx={{
                      fontWeight:
                        TYPOGRAPHY_COMMON_STYLES.fontWeightSemiBold,
                      fontSize: 14,
                      lineHeight: "18px",
                      color: "#90959E",
                      "&:hover": {
                        backgroundColor: "#F6F8FA",
                        borderRadius: 5,
                        color: COLORS.Romance,
                      },
                      "&.Mui-selected": {
                        backgroundColor: "#F6F8FA",
                        borderRadius: 5,
                        color: COLORS.Romance,
                      },
                    }}>
                      {site.siteName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </Box>
          <Box sx={{
            width: "100%",
            display: "flex",
            justifyContent: "flex-end",
            gap: "16px",
            alignItems: "center",
            p: "32px",
          }}>
            <Button
              variant="text"
              color="primary"
              sx={{ width: "140px", height: "36px", fontSize: "14px", fontWeight: "600", color: "#6B7280", borderRadius: "8px" }}
              onClick={onClose}
            >
              Back
            </Button>
            <Button
              variant="contained"
              color="primary"
              sx={{ width: "140px", height: "36px", fontSize: "14px", fontWeight: "600", color: COLORS.white, borderRadius: "8px" }}
              onClick={handleNextClick}
              disabled={!currentSiteSelected}
            >
              Next
            </Button>
          </Box>
        </Box>
      </Box>
    </Modal>
  );
};
